import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs/operators';

import { ChangeClosingDateComponent } from './components/change-closing-date/change-closing-date.component';
import { DealStatusEnum } from './modules/advance-application/models/deal-status.enum';
import { DealService } from './modules/advance-application/services/deal.service';
import { User } from './modules/authentication/models/v1-api.interface';
import { AuthenticationService } from './modules/authentication/services/authentication.service';
import { ProfileService } from './modules/profile/services/profile.service';
import { AgreementService } from './modules/shared/services/agreement.service';
import { AlertsService } from './modules/shared/services/alerts.service';
import { DateService } from './modules/shared/services/date.service';
import { LocaliseService } from './modules/shared/services/localise.service';
import { ThemesService } from './services/themes.service';

export const initializeApp =
  (
    authenticationService: AuthenticationService,
    dealService: DealService,
    alertsService: AlertsService,
    router: Router,
    agreementService: AgreementService,
    profileService: ProfileService,
    dateService: DateService,
    toastrService: ToastrService,
    dialog: MatDialog,
    localiseService: LocaliseService,
    themesService: ThemesService,
  ) =>
  async () => {
    if (location.pathname === '/astra/finished') {
      return;
    }

    const final = () => {
      const spinner = document.getElementById('launch-spinner');

      if (spinner) {
        spinner.style.display = 'none';
      }
    };

    const ppKey = new URLSearchParams(window.location.search).get('ppkey');
    if (ppKey) {
      const now = new Date();
      const time = now.getTime();
      const days60InMilliseconds = 60 * 60 * 24 * 60 * 1000;
      const expireTime = time + days60InMilliseconds;
      now.setTime(expireTime);
      document.cookie = `ppkey=${ppKey};expires=${now.toUTCString()};path=/`;
    }

    let userPromise: Promise<void | User>;
    if (document.cookie.includes('sess=')) {
      userPromise = authenticationService
        .getProfile()
        .toPromise()
        .catch(() => {});
    } else {
      userPromise = Promise.resolve();
    }

    const [user] = await Promise.all([
      userPromise,
      localiseService.getTranslations().catch((e) => {
        console.error('getTranslations()', e);
        // use continue without translations
      }),
    ]);

    if (!user?.id) {
      if (location.pathname === '/') {
        await router.navigate(['/login'], { queryParamsHandling: 'preserve' });
      } else if (location.pathname.startsWith('/signup-old')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });

        await router.navigate(['/signup-old'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/signup')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });
        await router.navigate(['/signup'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/invite-signup-old')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });

        await router.navigate(['/invite-signup-old'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/invite-signup')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });

        await router.navigate(['/invite-signup'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/invite-agent')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });
        await router.navigate(['/invite-agent'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/request-to-join-brokerage')) {
        const queryParams: Record<string, any> = {};
        new URLSearchParams(window.location.search).forEach((value, key) => {
          queryParams[key] = value;
        });
        await router.navigate(['/request-to-join-brokerage'], {
          queryParamsHandling: 'merge',
          queryParams,
        });
      } else if (location.pathname.startsWith('/prequalify')) {
        await router.navigate(['/prequalify'], {
          queryParamsHandling: 'preserve',
        });
      } else if (
        !location.pathname.startsWith('/login') &&
        !location.pathname.indexOf('?')
      ) {
        await router.navigate(['/login'], {
          queryParamsHandling: 'preserve',
        });
      }

      return final();
    } else if (
      location.pathname.startsWith('/login') &&
      location.search.includes('loginAsToken=')
    ) {
      await authenticationService.logout(true).toPromise();
      return final();
    }

    if (dealService.isNotFinishedSignup(user)) {
      dealService.navigateToSignup(user);
      return final();
    }

    agreementService
      .getLastAgreementForReview(user.lastAgreementEffectiveDateReviewed)
      .subscribe(
        (lastAgreement) => {
          if (!lastAgreement) {
            return;
          }

          alertsService.addAlert({
            text: 'We have updated our terms and conditions.',
            confirmText: 'Learn More',
            confirm: (a) => {
              router.navigate(['/sign-user-agreement'], {
                queryParams: { id: lastAgreement.id },
              });
            },
          });
        },
        () => {
          // skip
        },
      );

    const closingDateTo = dateService.addDaysToDate(
      new Date(),
      DealService.confirmBeforeClosingDays,
    );
    dealService
      .getList({
        limit: 5,
        offset: 0,
        confirmedClosingDate: false,
        closingDateTo: dateService.dateToYyyymmdd(closingDateTo),
        closingDateFrom: dateService.dateToYyyymmdd(new Date()),
        status: [DealStatusEnum.Approved],
      })
      .subscribe(
        (notConfirmedDeals) => {
          if (!notConfirmedDeals?.list) {
            return;
          }

          notConfirmedDeals.list.forEach((notConfirmedDeal) => {
            alertsService.addAlert({
              text: `Is ${
                notConfirmedDeal.line1Address
              } still due to close on ${dateService.dateFormat(
                notConfirmedDeal.closingDate,
              )}?`,
              confirmText: 'Yes',
              cancelText: 'No',
              notRemoveYourself: true,
              confirm: (a) =>
                dealService.confirmDeal(notConfirmedDeal.id).subscribe(() => {
                  toastrService.success('Closing date confirmed');
                  alertsService.removeAlert(a.id);
                }),
              cancel: (a) => {
                const ref = dialog.open(ChangeClosingDateComponent, {
                  panelClass: themesService.getDarkThemeClass(),
                });
                ref.componentInstance.deal = notConfirmedDeal;
                ref
                  .afterClosed()
                  .pipe(filter((v) => v))
                  .subscribe(() => alertsService.removeAlert(a.id));
              },
            });
          });
        },
        () => {
          // skip
        },
      );

    if (location.pathname === '/') {
      await router.navigate(['/dashboard'], {
        queryParamsHandling: 'preserve',
      });
    }

    final();
  };
